import { formatPropertyName } from "@/utils/formatPropertyName"
import { toRaw } from "vue"
import {
	ArraySchema,
	BooleanSchema,
	EnumSchema,
	NullableSchema,
	ObjectSchema,
	OptionalSchema,
	StringSchema,
	type ApiSchema,
} from "vue-utils"
import { Operator } from "./Filter"

export class ObjectSchemaProperty {
	readonly key: string[]
	readonly rawSchema: ApiSchema<unknown>
	readonly usedSchema: ApiSchema<unknown>

	constructor(key: string[], rawSchema: ApiSchema<unknown>, usedSchema: ApiSchema<unknown>) {
		this.key = key
		this.rawSchema = rawSchema
		this.usedSchema = usedSchema
	}

	static getNestedProperties<T extends object>(schema: ObjectSchema<T>) {
		const result: ObjectSchemaProperty[] = []
		ObjectSchemaProperty.addNestedProperties([], result, schema)
		return result
	}

	private static addNestedProperties<T extends object>(
		pathPrefix: string[],
		result: ObjectSchemaProperty[],
		schema: ObjectSchema<T>
	) {
		const properties = schema.properties
		for (const propertyName in properties) {
			const propertySchema = properties[propertyName] as ApiSchema<unknown>
			const usedSchema = ObjectSchemaProperty.getUsedSchema(propertySchema)

			if (usedSchema instanceof ObjectSchema) {
				ObjectSchemaProperty.addNestedProperties([...pathPrefix, propertyName], result, usedSchema)
			} else if (usedSchema instanceof ArraySchema) {
				console.warn(
					`Don't know what to do with ${[...pathPrefix, propertyName].join(".")} of schema type ${String(propertySchema)}`
				)
			} else {
				const key = [...pathPrefix, propertyName]
				result.push(new ObjectSchemaProperty(key, propertySchema, usedSchema))
			}
		}
	}

	private static getUsedSchema(schema: ApiSchema<unknown>): ApiSchema<unknown> {
		if (schema instanceof NullableSchema || schema instanceof OptionalSchema) {
			return schema.schema
		}
		return schema
	}

	getValidOperators(): Operator[] {
		const operators = new Set<Operator>([Operator.Equals, Operator.NotEquals, Operator.In, Operator.NotIn])

		const rawSchema = this.rawSchema
		const usedSchema = this.usedSchema

		if (rawSchema instanceof NullableSchema || usedSchema === String || usedSchema === StringSchema) {
			operators.add(Operator.Exists)
			operators.add(Operator.NotExists)
		}
		if (
			!(
				usedSchema instanceof EnumSchema ||
				usedSchema === String ||
				usedSchema === StringSchema ||
				usedSchema === Boolean ||
				usedSchema === BooleanSchema
			)
		) {
			operators.add(Operator.GreaterThan)
			operators.add(Operator.LessThan)
		}
		if (usedSchema === String || usedSchema === StringSchema) {
			operators.add(Operator.StringContains)
			operators.add(Operator.StringNotContains)
		}

		return Array.from(operators)
	}

	getValue(object: object): unknown {
		let i = 0
		while (i < this.key.length) {
			if (typeof object !== "object") {
				throw new Error("Invalid key, property is not an object")
			}
			object = (object as Record<string, object>)[this.key[i++]]
		}
		return object
	}

	formatName(): string {
		return formatPropertyName(this.key)
	}

	vueKey(): string {
		return this.key.join(":")
	}

	equals(other: ObjectSchemaProperty) {
		if (toRaw(other) === this) {
			return true
		}
		return this.matchesKey(other.key)
	}

	matchesKey(otherKey: string[]) {
		if (otherKey.length !== this.key.length) {
			return false
		}
		for (let i = 0; i < this.key.length; i++) {
			if (this.key[i] !== otherKey[i]) {
				return false
			}
		}
		return true
	}
}
